import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import axios from "./axios";
import { apiErrorMessage } from "../utils/apiError";

export const companiesKeys = {
  companie: () => ["companie"] as const,
  companieRental: () => ["companieRental"] as const,
  companieByID: (id: number) =>
    [...companiesKeys.companie(), "id", id] as const,
};

export const useGetOneCompanie = (id: number) => {
  const { data, error, ...res } = useQuery({
    queryKey: companiesKeys.companieByID(id),
    queryFn: async (): Promise<TCompanie> => {
      const data = await axios.get(`/companies/${id}`);
      return data.data;
    },
    enabled: !!id,
  });
  if (error) {
  }
  return { companie: data, ...res };
};

export const useGetAllCompaniesRental = () => {
  const { data, error, ...res } = useQuery({
    queryKey: companiesKeys.companieRental(),
    queryFn: async (): Promise<TCompanie[]> => {
      const data = await axios.get(`/fleets/rental`);
      return data?.data;
    },
  });
  if (error) {
  }
  return { rentalCompanies: data ?? [], ...res };
};

export interface RequestUpdateCompanyVat {
  /** VAT RATE as a percentage. `company.vat` is decimal(10,2), 0–100. */
  vat: number;
}

export interface RequestUpdateCompany {
  name?: string;
  registration_id?: string;
  logo?: string;
  vat?: number;
}

/**
 * Update the company profile (name, registration id, logo, vat).
 * Backend: PUT /companies/{companyId}  (gate: ONLY_FLEET_ADMIN)
 *
 * Optimistically patches the cached company so the settings screen (and
 * anything else reading name/logo) reflects the change immediately, then
 * invalidates to reconcile with the server.
 */
export const useUpdateCompany = (companyId: number) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: RequestUpdateCompany) => {
      if (!companyId) {
        throw new Error("Company ID is required");
      }
      const { data } = await axios.put(`/companies/${companyId}`, payload);
      return data;
    },
    onSuccess: (_data, variables) => {
      const key = companiesKeys.companieByID(companyId);
      const prev = queryClient.getQueryData<TCompanie>(key);
      if (prev) {
        queryClient.setQueryData<TCompanie>(key, { ...prev, ...variables });
      }
      queryClient.invalidateQueries({ queryKey: key });
      toast.success(t("common.changeSaved", "Change saved"));
    },
    onError: (err: any) => {
      toast.error(
        apiErrorMessage(err, t("common.errorGlobal", "An error occurred"))
      );
    },
  });
};

/**
 * Update the company VAT rate.
 * Backend: PUT /companies/{companyId}/vat/update  (gate: ONLY_FLEET_ADMIN)
 *
 * On success we optimistically patch the cached company so every screen that
 * reads `vat` (TVA section, TCO, billing…) reflects the new rate immediately,
 * then invalidate to reconcile with the server value.
 */
export const useUpdateCompanyVat = (companyId: number) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: RequestUpdateCompanyVat) => {
      if (!companyId) {
        throw new Error("Company ID is required");
      }
      const { data } = await axios.put(
        `/companies/${companyId}/vat/update`,
        payload
      );
      return data;
    },
    onSuccess: (_data, variables) => {
      const key = companiesKeys.companieByID(companyId);
      const prev = queryClient.getQueryData<TCompanie>(key);
      if (prev) {
        queryClient.setQueryData<TCompanie>(key, {
          ...prev,
          vat: variables.vat,
        });
      }
      queryClient.invalidateQueries({ queryKey: key });
      toast.success(t("common.changeSaved", "Change saved"));
    },
    onError: (err: any) => {
      toast.error(
        err?.response?.data?.message ??
          t("common.errorGlobal", "An error occurred")
      );
    },
  });
};
